import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  History, CheckCircle, XCircle, Clock, RefreshCw, Play,
  ChevronRight, Activity, AlertTriangle, TrendingUp, Zap,
  Timer, DollarSign, Code, GitBranch, BarChart3, Layout
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function ExecutionHistoryDashboard() {
  const [executions, setExecutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [executionSteps, setExecutionSteps] = useState(null);
  const [viewMode, setViewMode] = useState("timeline"); // timeline or flowchart

  const fetchExecutions = async () => {
    try {
      const response = await axios.get(`${API}/executions?limit=50`, { withCredentials: true });
      setExecutions(response.data);
    } catch (error) {
      console.error('Error fetching executions:', error);
      toast.error("Failed to load executions");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/executions/stats/summary?days=30`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchExecutions();
    fetchStats();
  }, []);

  const viewDetails = async (executionId) => {
    try {
      const [execResponse, stepsResponse] = await Promise.all([
        axios.get(`${API}/executions/${executionId}`, { withCredentials: true }),
        axios.get(`${API}/executions/${executionId}/steps`, { withCredentials: true })
      ]);
      setSelectedExecution(execResponse.data);
      setExecutionSteps(stepsResponse.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error("Failed to load execution details");
    }
  };

  const replayExecution = async (executionId, fromStep = 0) => {
    try {
      const response = await axios.post(
        `${API}/executions/${executionId}/replay`,
        { from_step: fromStep },
        { withCredentials: true }
      );
      toast.success(`Workflow replayed! New execution: ${response.data.new_execution_id}`);
      fetchExecutions();
      setShowDetailsModal(false);
    } catch (error) {
      toast.error("Failed to replay execution");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <Clock className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStepStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Clock className="w-4 h-4 text-blue-500 animate-pulse" />;
      case 'skipped':
        return <ChevronRight className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/10 text-green-500 border-green-500/20',
      failed: 'bg-red-500/10 text-red-500 border-red-500/20',
      running: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    };
    return styles[status] || 'bg-muted text-muted-foreground';
  };

  const formatDuration = (ms) => {
    if (!ms) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const calculateExecutionStats = () => {
    if (!executions.length) return { total: 0, completed: 0, failed: 0, running: 0 };
    return {
      total: executions.length,
      completed: executions.filter(e => e.status === 'completed').length,
      failed: executions.filter(e => e.status === 'failed').length,
      running: executions.filter(e => e.status === 'running').length,
    };
  };

  const localStats = calculateExecutionStats();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="execution-history-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">
              Execution History & Monitoring
            </h1>
            <p className="text-muted-foreground mt-1">
              Track workflow executions with detailed step-by-step insights
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => {
              fetchExecutions();
              fetchStats();
            }}
            className="rounded-full"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{localStats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Runs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{localStats.completed}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{localStats.failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {stats?.summary?.success_rate || 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Timer className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Duration</p>
                    <p className="text-xl font-semibold text-foreground">
                      {formatDuration(stats.summary.avg_duration_ms)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">API Calls</p>
                    <p className="text-xl font-semibold text-foreground">
                      {stats.resource_usage.api_calls}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Est. Cost</p>
                    <p className="text-xl font-semibold text-foreground">
                      ${stats.resource_usage.estimated_cost.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Executions */}
        <Card className="glass-card border-border">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5" /> Recent Executions
            </CardTitle>
            <CardDescription>
              Click on any execution to view detailed step-by-step breakdown
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-20 bg-muted/50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : executions.length > 0 ? (
              <div className="space-y-2">
                {executions.map((execution) => (
                  <div 
                    key={execution.execution_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/20 hover:bg-muted/40 transition-all cursor-pointer border border-transparent hover:border-primary/20"
                    onClick={() => viewDetails(execution.execution_id)}
                    data-testid={`execution-${execution.execution_id}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        execution.status === 'completed' ? 'bg-green-500/10' :
                        execution.status === 'failed' ? 'bg-red-500/10' : 'bg-blue-500/10'
                      }`}>
                        {getStatusIcon(execution.status)}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">
                          {execution.workflow_name || 'Unnamed Workflow'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-sm font-mono text-muted-foreground">
                            {execution.execution_id}
                          </p>
                          {execution.duration_ms && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {formatDuration(execution.duration_ms)}
                            </span>
                          )}
                          {execution.nodes_executed > 0 && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <GitBranch className="w-3 h-3" />
                              {execution.nodes_succeeded}/{execution.nodes_executed} steps
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden md:block">
                        <p className="text-sm text-foreground">
                          {new Date(execution.started_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(execution.started_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <Badge variant="outline" className={getStatusBadge(execution.status)}>
                        {execution.status}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <History className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium text-foreground">No executions yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Execute a published workflow to see detailed tracking here
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Execution Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-3">
              {selectedExecution && getStatusIcon(selectedExecution.status)}
              <span>Execution Details</span>
              {selectedExecution && (
                <Badge variant="outline" className={getStatusBadge(selectedExecution.status)}>
                  {selectedExecution.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedExecution && executionSteps && (
            <div className="flex-1 overflow-hidden">
              <Tabs defaultValue="timeline" className="h-full flex flex-col" onValueChange={setViewMode}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="timeline" className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Timeline View
                  </TabsTrigger>
                  <TabsTrigger value="flowchart" className="flex items-center gap-2">
                    <Layout className="w-4 h-4" />
                    Flowchart View
                  </TabsTrigger>
                </TabsList>

                {/* Execution Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Workflow</p>
                    <p className="text-sm font-semibold text-foreground">{selectedExecution.workflow_name}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-semibold text-foreground">
                      {formatDuration(selectedExecution.duration_ms)}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Steps</p>
                    <p className="text-sm font-semibold text-foreground">
                      {selectedExecution.nodes_succeeded}/{selectedExecution.nodes_executed}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Trigger</p>
                    <p className="text-sm font-semibold text-foreground capitalize">
                      {selectedExecution.trigger_type || 'manual'}
                    </p>
                  </div>
                </div>

                {/* Timeline View */}
                <TabsContent value="timeline" className="flex-1 overflow-hidden mt-0">
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-4">
                      {executionSteps.steps && executionSteps.steps.length > 0 ? (
                        executionSteps.steps.map((step, index) => (
                          <div key={step.step_id} className="relative pl-8 pb-4">
                            {/* Timeline line */}
                            {index < executionSteps.steps.length - 1 && (
                              <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-border" />
                            )}
                            
                            {/* Step indicator */}
                            <div className="absolute left-0 top-1 w-8 h-8 rounded-full bg-background border-2 flex items-center justify-center"
                                 style={{
                                   borderColor: 
                                     step.status === 'success' ? '#22c55e' :
                                     step.status === 'failed' ? '#ef4444' :
                                     step.status === 'running' ? '#3b82f6' : '#9ca3af'
                                 }}>
                              {getStepStatusIcon(step.status)}
                            </div>

                            <Card className={`ml-4 ${
                              step.status === 'failed' ? 'border-red-500/50 bg-red-500/5' : 'border-border'
                            }`}>
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-semibold text-foreground">
                                        {step.node_label}
                                      </h4>
                                      <Badge variant="outline" className="text-xs">
                                        {step.node_type}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground font-mono">
                                      Step {index + 1} • {step.step_id}
                                    </p>
                                  </div>
                                  {step.duration_ms > 0 && (
                                    <div className="text-right">
                                      <p className="text-xs text-muted-foreground">Duration</p>
                                      <p className="text-sm font-semibold text-foreground">
                                        {formatDuration(step.duration_ms)}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {step.retry_count > 0 && (
                                  <div className="mb-2 p-2 bg-amber-500/10 rounded border border-amber-500/20">
                                    <p className="text-xs text-amber-600 dark:text-amber-400">
                                      Retried {step.retry_count} time(s)
                                    </p>
                                  </div>
                                )}

                                {step.error && (
                                  <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <p className="text-xs font-medium text-red-500 mb-1">Error</p>
                                    <p className="text-xs text-red-400 font-mono">{step.error}</p>
                                  </div>
                                )}

                                {/* Input/Output Data */}
                                {(Object.keys(step.input_data || {}).length > 0 || 
                                  Object.keys(step.output_data || {}).length > 0) && (
                                  <div className="grid grid-cols-2 gap-2 mt-3">
                                    {Object.keys(step.input_data || {}).length > 0 && (
                                      <div className="p-2 bg-muted/50 rounded text-xs">
                                        <p className="font-medium text-muted-foreground mb-1">Input</p>
                                        <pre className="text-xs overflow-x-auto">
                                          {JSON.stringify(step.input_data, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    {Object.keys(step.output_data || {}).length > 0 && (
                                      <div className="p-2 bg-muted/50 rounded text-xs">
                                        <p className="font-medium text-muted-foreground mb-1">Output</p>
                                        <pre className="text-xs overflow-x-auto">
                                          {JSON.stringify(step.output_data, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Step Logs */}
                                {step.logs && step.logs.length > 0 && (
                                  <details className="mt-3">
                                    <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                      View Logs ({step.logs.length})
                                    </summary>
                                    <div className="mt-2 p-2 bg-muted/30 rounded-lg space-y-1 max-h-32 overflow-y-auto">
                                      {step.logs.map((log, i) => (
                                        <div key={i} className="text-xs font-mono">
                                          <span className="text-muted-foreground">
                                            {new Date(log.timestamp).toLocaleTimeString()}
                                          </span>
                                          {' '}
                                          <span className={
                                            log.level === 'error' ? 'text-red-500' :
                                            log.level === 'warning' ? 'text-amber-500' :
                                            'text-foreground'
                                          }>
                                            {log.message}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No step details available
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Flowchart View */}
                <TabsContent value="flowchart" className="flex-1 overflow-hidden mt-0">
                  <ScrollArea className="h-[500px]">
                    <div className="p-4">
                      {executionSteps.steps && executionSteps.steps.length > 0 ? (
                        <div className="flex flex-col items-center gap-3">
                          {executionSteps.steps.map((step, index) => (
                            <div key={step.step_id} className="w-full max-w-2xl">
                              <Card className={`${
                                step.status === 'failed' ? 'border-red-500/50 bg-red-500/5' :
                                step.status === 'success' ? 'border-green-500/50 bg-green-500/5' :
                                'border-border'
                              }`}>
                                <CardContent className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                                         style={{
                                           backgroundColor: 
                                             step.status === 'success' ? 'rgba(34, 197, 94, 0.1)' :
                                             step.status === 'failed' ? 'rgba(239, 68, 68, 0.1)' :
                                             'rgba(156, 163, 175, 0.1)'
                                         }}>
                                      {getStepStatusIcon(step.status)}
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="font-semibold text-foreground">{step.node_label}</h4>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-xs">{step.node_type}</Badge>
                                        {step.duration_ms > 0 && (
                                          <span className="text-xs text-muted-foreground">
                                            {formatDuration(step.duration_ms)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {step.error && (
                                    <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-500">
                                      {step.error}
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                              {index < executionSteps.steps.length - 1 && (
                                <div className="flex justify-center my-2">
                                  <ChevronRight className="w-6 h-6 text-muted-foreground rotate-90" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          No step details available
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>

              {/* Action Buttons */}
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Execution ID: <span className="font-mono">{selectedExecution.execution_id}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDetailsModal(false)}
                  >
                    Close
                  </Button>
                  {selectedExecution.status !== 'running' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => replayExecution(selectedExecution.execution_id)}
                      data-testid="replay-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Replay Workflow
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
